import { useState } from 'react'
import { Trash2 } from 'lucide-react'
import { Button } from '../ui'
import { useAuth } from '../AuthProvider'
import { useI18n } from '../../i18n'
import { API_ENDPOINTS } from '../../config/api'
import { GovernanceListingSection } from '../governance/GovernanceListingSection'
import OrganizationDeletionRequestModal from './OrganizationDeletionRequestModal'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import adminStyles from '@/styles/pages/AdminPanel.module.scss'

export type OrganizationDeletionRecord = {
  id: string
  name: string
  userCount?: number
  assetCount?: number
  deletionStatus?: 'ACTIVE' | 'PENDING_DELETION'
  deletionScheduledFor?: string | null
}

type Props = {
  organizations: OrganizationDeletionRecord[]
  onRefresh?: () => void | Promise<void>
}

export default function OrganizationDeletionAdminSection({ organizations, onRefresh }: Props) {
  const { t } = useI18n()
  const { token } = useAuth()
  const [deletionTarget, setDeletionTarget] = useState<OrganizationDeletionRecord | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  const openDeletionModal = (org: OrganizationDeletionRecord) => {
    if (org.deletionStatus === 'PENDING_DELETION') return
    setSuccessMessage('')
    setDeletionTarget(org)
  }

  const closeDeletionModal = () => {
    if (submitting) return
    setDeletionTarget(null)
  }

  const submitDeletion = async (payload: { confirmationName: string; comment: string }) => {
    if (!deletionTarget) return
    setSubmitting(true)
    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_ORG_REQUEST_DELETION(deletionTarget.id), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })
      const body = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(body?.error || body?.message || 'Failed to schedule organization deletion')
      }
      const orgName = deletionTarget.name
      const scheduledFor = body?.data?.scheduledFor
        ? new Date(body.data.scheduledFor).toLocaleString()
        : null
      setDeletionTarget(null)
      setSuccessMessage(
        scheduledFor
          ? t(
              'admin.orgDeletion.successWithDate',
              'Deletion scheduled for {{name}}. Permanent removal is planned for {{date}}.',
              { name: orgName, date: scheduledFor },
            )
          : t('admin.orgDeletion.success', 'Deletion scheduled for {{name}}.', { name: orgName }),
      )
      await onRefresh?.()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {successMessage ? (
        <div className={adminStyles.alertSuccess} role="status">
          <p className={adminStyles.successTitle}>{t('common.success', 'Success')}</p>
          <p className={adminStyles.successBody}>{successMessage}</p>
        </div>
      ) : null}

      <GovernanceListingSection
        title={t('admin.organizationsDeletion.title', 'Organization deletion')}
        description={t(
          'admin.organizationsDeletion.description',
          'Deletion is soft: request with mandatory comment, hard-delete after 5 days.',
        )}
      >
        <div className={listStyles.metricsGrid}>
          {organizations.length === 0 ? (
            <div className={listStyles.panelSubtitle}>
              {t('admin.organizationsDeletion.empty', 'No organizations found.')}
            </div>
          ) : (
            organizations.map((org) => (
              <article key={org.id} className={listStyles.panel}>
                <h3 className={listStyles.panelTitle}>{org.name}</h3>
                <p className={listStyles.panelSubtitle}>
                  {t('admin.organizationsDeletion.users', 'Users')}: {org.userCount ?? 0}
                  {org.assetCount != null
                    ? ` · ${t('admin.organizationsDeletion.assets', 'Assets')}: ${org.assetCount}`
                    : ''}
                </p>
                {org.deletionStatus === 'PENDING_DELETION' ? (
                  <p className={listStyles.panelSubtitle}>
                    {t('admin.organizationsDeletion.pendingUntil', 'Pending deletion until')}{' '}
                    {org.deletionScheduledFor
                      ? new Date(org.deletionScheduledFor).toLocaleString()
                      : t('admin.organizationsDeletion.dateUnavailable', 'scheduled date unavailable')}
                  </p>
                ) : null}
                <div className={adminStyles.actionGroup}>
                  <Button
                    type="button"
                    size="small"
                    color="outline"
                    icon={<Trash2 className={adminStyles.iconSm} aria-hidden />}
                    onClick={() => openDeletionModal(org)}
                    disabled={org.deletionStatus === 'PENDING_DELETION'}
                  >
                    {org.deletionStatus === 'PENDING_DELETION'
                      ? t('admin.organizationsDeletion.scheduled', 'Deletion scheduled')
                      : t('admin.organizationsDeletion.request', 'Request deletion')}
                  </Button>
                </div>
              </article>
            ))
          )}
        </div>
      </GovernanceListingSection>

      <OrganizationDeletionRequestModal
        open={deletionTarget != null}
        organization={deletionTarget}
        submitting={submitting}
        onClose={closeDeletionModal}
        onSubmit={submitDeletion}
      />
    </>
  )
}
